import React, { useMemo, useState } from "react";
import {
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  Inbox,
  Mail,
  Phone,
  Eye,
} from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 8;

// Show a date like "12 Mar 2025, 14:30".
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// is_read may arrive as true, 1 or "1".
const isReadValue = (c) =>
  c.is_read === true || c.is_read === 1 || c.is_read === "1";

const ContactsTable = ({
  contacts = [],
  loading = false,
  onToggleRead,
  onView,
}) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [readStatus, setReadStatus] = useState("all");
  const [page, setPage] = useState(1);
  // Column sort — newest first by default.
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  // Category options taken from the data.
  const categoryOptions = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => c.category && set.add(c.category));
    return [...set];
  }, [contacts]);

  // Filter by search query + category.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((c) => {
      const name = (c.full_name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const company = (c.company_name || "").toLowerCase();
      const phone = (c.phone_number || "").toLowerCase();
      const cat = c.category || "";
      const matchesQuery =
        !q ||
        name.includes(q) ||
        email.includes(q) ||
        company.includes(q) ||
        phone.includes(q);
      const matchesCategory = category === "all" || cat === category;
      const matchesRead =
        readStatus === "all" ||
        (readStatus === "read" ? isReadValue(c) : !isReadValue(c));
      return matchesQuery && matchesCategory && matchesRead;
    });
  }, [contacts, query, category, readStatus]);

  // Value used for comparing two rows on the active sort column.
  const sortValue = (c, key) => {
    switch (key) {
      case "name":
        return (c.full_name || "").toLowerCase();
      case "email":
        return (c.email || "").toLowerCase();
      case "phone":
        return (c.phone_number || "").toLowerCase();
      case "company":
        return (c.company_name || "").toLowerCase();
      case "category":
        return (c.category || "").toLowerCase();
      case "date":
        return new Date(c.created_at || 0).getTime();
      case "read":
        return isReadValue(c) ? 1 : 0;
      default:
        return "";
    }
  };

  // Sort the filtered rows by the active column + direction.
  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Click a header: flip direction if it's the active column, else sort it ascending.
  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
    setPage(1);
  };

  const sortIcon = (key) => {
    if (sort.key !== key)
      return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sort.dir === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const handleCategory = (value) => {
    setCategory(value);
    setPage(1);
  };

  const handleReadStatus = (value) => {
    setReadStatus(value);
    setPage(1);
  };

  const thClass =
    "text-gray-500 font-semibold uppercase text-[11px] tracking-wider";

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={handleSearch}
            placeholder="Search by name, email, phone or company…"
            className="w-full rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition"
          />
        </div>

        <Select value={category} onValueChange={handleCategory}>
          <SelectTrigger className="w-[180px] h-auto rounded-md border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categoryOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={readStatus} onValueChange={handleReadStatus}>
          <SelectTrigger className="w-[150px] h-auto rounded-md border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="unread">Unread</SelectItem>
          </SelectContent>
        </Select>

        <span className="ml-auto shrink-0 text-sm text-gray-500">
          {filtered.length} {filtered.length === 1 ? "contact" : "contacts"}
        </span>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b border-gray-200">
            <TableHead className={`${thClass} pl-6`}>
              <button
                type="button"
                onClick={() => toggleSort("name")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Name
                {sortIcon("name")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("email")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Email
                {sortIcon("email")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("phone")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Phone
                {sortIcon("phone")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("company")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Company
                {sortIcon("company")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("category")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Category
                {sortIcon("category")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("date")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Date
                {sortIcon("date")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("read")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Read
                {sortIcon("read")}
              </button>
            </TableHead>
            <TableHead className={`${thClass} text-right pr-6`}>View</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-gray-500 py-12">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading contacts…
                </span>
              </TableCell>
            </TableRow>
          ) : pageItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-12">
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <Inbox className="h-8 w-8" />
                  <span className="text-sm">
                    {query || category !== "all"
                      ? "No contacts match your filters."
                      : "No contact submissions yet."}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            pageItems.map((contact) => {
              const read = isReadValue(contact);

              return (
                <TableRow
                  key={contact.id}
                  className={`border-b border-gray-100 last:border-0 hover:bg-gray-50/60 ${
                    read ? "" : "bg-[#b32126]/[0.03]"
                  }`}
                >
                  {/* Name — click to view the full message */}
                  <TableCell className="font-medium text-gray-900 pl-6 py-4">
                    <button
                      type="button"
                      onClick={() => onView?.(contact)}
                      className="flex items-center gap-2 text-left hover:text-[#b32126] transition-colors"
                    >
                      {!read && (
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-[#b32126]"
                          title="Unread"
                        />
                      )}
                      {contact.full_name || "—"}
                    </button>
                  </TableCell>

                  {/* Email */}
                  <TableCell className="py-4 text-sm">
                    {contact.email ? (
                      <a
                        href={`mailto:${contact.email}`}
                        className="inline-flex items-center gap-1.5 text-gray-700 hover:text-[#b32126]"
                      >
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        {contact.email}
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Phone */}
                  <TableCell className="py-4 text-sm">
                    {contact.phone_number ? (
                      <a
                        href={`tel:${contact.phone_number}`}
                        className="inline-flex items-center gap-1.5 text-gray-500 hover:text-[#b32126]"
                      >
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        {contact.phone_number}
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Company */}
                  <TableCell className="text-gray-600 py-4">
                    {contact.company_name || <span className="text-gray-400">—</span>}
                  </TableCell>

                  {/* Category */}
                  <TableCell className="py-4">
                    {contact.category ? (
                      <span className="inline-flex items-center rounded-full bg-[#b32126]/10 px-2.5 py-0.5 text-xs font-medium text-[#b32126]">
                        {contact.category}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Date */}
                  <TableCell className="text-gray-600 py-4 whitespace-nowrap">
                    {formatDate(contact.created_at)}
                  </TableCell>

                  {/* Read toggle */}
                  <TableCell className="py-4">
                    <button
                      type="button"
                      onClick={() => onToggleRead?.(contact)}
                      title={read ? "Mark as unread" : "Mark as read"}
                      className={`relative h-6 w-11 rounded-full transition-colors ${
                        read ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          read ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </TableCell>

                  {/* View */}
                  <TableCell className="py-4 pr-6">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => onView?.(contact)}
                        title="View message"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-200 text-gray-600 hover:border-[#b32126] hover:text-[#b32126] transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-medium transition-colors ${
                  p === currentPage
                    ? "bg-[#b32126] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsTable;
